import { Component, effect, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { catchError, exhaustMap, filter, merge, of, Subject, timer } from 'rxjs';
import { AuthService } from '../../core/auth/services/auth.service';
import { BusinessNotificationsService } from './business-notifications.service';
import { BusinessNotification } from './notification.models';

@Component({
  selector: 'app-notification-bell',
  imports: [DatePipe, RouterLink, MatIconModule, MatMenuModule, MatButtonModule],
  template: `
    <button mat-button [matMenuTriggerFor]="menu" (menuOpened)="refresh.next()" aria-label="Ouvrir les notifications">
      <mat-icon fontSet="material-symbols-outlined">notifications</mat-icon> Notifications
      @if (hasUnread()) { <span class="dot" aria-label="Notifications récentes non lues"></span> }
    </button>
    <mat-menu #menu="matMenu">
      @if (unavailable()) { <p class="hint" role="status">Notifications indisponibles. Réessayez dans votre boîte de réception.</p> }
      @for (item of items().slice(0, 5); track item.id) {
        <a mat-menu-item routerLink="/notifications">
          <mat-icon fontSet="material-symbols-outlined">{{ item.readAt ? 'drafts' : 'mark_email_unread' }}</mat-icon>
          <span>{{ item.title }}<small>{{ item.createdAt | date:'dd/MM/yyyy HH:mm' }}</small></span>
        </a>
      } @empty {
        @if (!unavailable()) { <p class="hint">Aucune notification récente.</p> }
      }
      <a mat-menu-item routerLink="/notifications"><mat-icon fontSet="material-symbols-outlined">inbox</mat-icon> Voir toutes les notifications</a>
    </mat-menu>
  `,
  styles: [`.dot{width:8px;height:8px;border-radius:50%;background:#d83d42;margin-left:8px;display:inline-block}small{display:block;font-size:11px;color:#64748b}.hint{max-width:280px;padding:12px 16px;font-size:13px}`],
})
export class NotificationBellComponent {
  private readonly auth = inject(AuthService);
  private readonly api = inject(BusinessNotificationsService);
  readonly items = signal<BusinessNotification[]>([]);
  readonly unavailable = signal(false);
  readonly refresh = new Subject<void>();
  readonly hasUnread = () => this.items().some(item => !item.readAt);
  constructor() {
    effect(onCleanup => {
      const identity = `${this.auth.organizationId}:${this.auth.keyCloakId}`;
      this.items.set([]);
      this.unavailable.set(false);
      if (!this.auth.isLoggedIn || !this.auth.organizationId || !identity) return;
      const subscription = merge(timer(0, 60000), this.refresh, this.api.changed).pipe(
        filter(() => document.visibilityState !== 'hidden'),
        exhaustMap(() => this.api.inbox(0, 50, true).pipe(catchError(() => of(null)))),
      ).subscribe(rows => {
        this.unavailable.set(rows === null);
        if (rows) this.items.set(rows);
      });
      onCleanup(() => subscription.unsubscribe());
    });
  }
}
